import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { stringify } from 'query-string';
import { InView } from 'react-intersection-observer';
import RightArrow from 'components/shared/SVG/ArrowRight';
import Grid from 'components/designSystem/Grid/Grid.tsx';
import { CustomerContext } from 'context/customer';

import geocode from 'helpers/geocode';
import { readAddressFromLocalStorage, readFulfillmentFromLocalStorage } from 'helpers/localStorage';
import { logMparticleEvent, mParticleCustomEvents, mParticleEventTypes } from 'helpers/mParticle';

import ButtonIcon from 'components/designSystem/ButtonIcon/ButtonIcon';

import styles from './style.module.scss';
import LocationSearch from '../../../shared/LocationSearch/LocationSearch';

function Hero({ handleStickyStateChange }) {
  const { user } = useContext(CustomerContext);
  const [formattedAddress, setFormattedAddress] = useState('');
  const [arrowButtonEnabled, setArrowButtonEnabled] = useState(false);
  useEffect(() => {
    const addressFromLocalStorage = readAddressFromLocalStorage().formatted_address;
    // If there is a valid address from local storage enable to arrow button
    if (addressFromLocalStorage) {
      setFormattedAddress(addressFromLocalStorage);
      setArrowButtonEnabled(true);
    }
  }, []);
  const router = useRouter();

  const handleSelectAddress = async (address) => {
    if (!address) return;

    const url = {
      pathname: 'search',
      search: stringify({
        loc: address.formatted_address,
        ...readFulfillmentFromLocalStorage(),
      }),
    };
    router.push(url, null);
  };

  // Triggered when hitting the arrow icon button (as opposed to selecting address from dropdown)
  const handleSearch = async () => {
    if (!formattedAddress) {
      return;
    }

    const result = await geocode({ address: formattedAddress });
    if (result) {
      const address = result[0];
      logMparticleEvent({
        eventName: mParticleCustomEvents.performLocationSearch,
        eventType: mParticleEventTypes.Other,
        attributes: {
          source: 'homepage',
          location: 'localstorage_search_bar',
          searched_location: address,
          searched_latitude: address?.geometry?.location?.lat().toString(),
          searched_longitude: address?.geometry?.location?.lng().toString(),
          user_is_authenticated: !!user,
        },
      });

      handleSelectAddress(address);
    }
  };

  function updateArrowButton(state) {
    setArrowButtonEnabled(state);
  }

  return (
    <section className={styles.heroSectionContainer}>
      <Grid container className={styles.heroContainer}>
        <Grid sm={4} md={6} lg={6} className={styles.heroImageCol}>
          <div className={styles.heroImage} />
        </Grid>
        <Grid sm={4} md={6} lg={6} className={styles.heroCopyCol}>
          <div className={styles.heroCopyWrap}>
            <h1 className={styles.heroCopy} data-testid="HeroHeader">
              Place an order.
              <br />
              Make an impact.
            </h1>
            <InView className={styles.inView} onChange={handleStickyStateChange}>
              <div className={styles.searchWrapper}>
                <LocationSearch
                  initialAddress={formattedAddress}
                  mparticleSource="homepage"
                  updateArrowButtonState={updateArrowButton}
                />
                <ButtonIcon
                  className={styles.arrowButton}
                  onClick={handleSearch}
                  disabled={!arrowButtonEnabled}
                >
                  <RightArrow />
                </ButtonIcon>
              </div>
            </InView>
          </div>
        </Grid>
      </Grid>
      <div className={styles.mobileRestaurantList}>
        <p className={styles.mobileRestaurantListCopy}>
          Own a restaurant?{' '}
          <a
            className={styles.mobileLearnMoreLink}
            href="https://get.chownow.com/get-started-cn?utm_medium=mobile_hero&utm_source=marketplace_web"
            target="_blank"
            rel="noreferrer"
          >
            ChowNow can help
          </a>
        </p>
      </div>
    </section>
  );
}

Hero.propTypes = {
  handleStickyStateChange: PropTypes.func,
};

Hero.defaultProps = {
  handleStickyStateChange: null,
};

export default Hero;
